import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Upload, CheckCircle2, AlertTriangle, XCircle, Loader2, ChevronRight, Info, RotateCw, Download, FileText, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SharedNav from '@/components/SharedNav';

const STAGES = [
  { id: 1, name: 'Structure', description: 'File parse & headers' },
  { id: 2, name: 'Missing', description: 'NaN & sentinel audit' },
  { id: 3, name: 'Duplicates', description: 'Exact / ID / near-dup' },
  { id: 4, name: 'Types', description: 'Range & extreme values' },
  { id: 5, name: 'Design', description: 'Replication schema' },
  { id: 6, name: 'Outliers', description: 'IQR / Z / Cook\'s' },
];

const statusStyles = {
  pass:  { bg: 'bg-emerald-50', ring: 'ring-emerald-200', icon: CheckCircle2,     fg: 'text-emerald-700', dot: 'bg-emerald-500' },
  warn:  { bg: 'bg-amber-50',   ring: 'ring-amber-300',  icon: AlertTriangle,    fg: 'text-amber-800',   dot: 'bg-amber-500' },
  block: { bg: 'bg-rose-50',    ring: 'ring-rose-300',   icon: XCircle,          fg: 'text-rose-800',    dot: 'bg-rose-500' },
  pending:{bg: 'bg-slate-50',   ring: 'ring-slate-200',  icon: Info,             fg: 'text-slate-500',   dot: 'bg-slate-300' },
};


// ═══════════════════════════════════════════════════════════════════
// Left-side stepper
// ═══════════════════════════════════════════════════════════════════
function Stepper({ currentStage, stageStates, onStageClick }) {
  return (
    <nav data-testid="clean-stepper" className="w-64 shrink-0 border-r border-slate-200 bg-white pr-6 py-6 flex flex-col gap-2">
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Validation pipeline</div>
      {STAGES.map((s, idx) => {
        const stResult = stageStates[s.id];
        const st = stResult?.status || 'pending';
        const isStale = !!stResult?.stale;
        const styles = statusStyles[st];
        const Ico = styles.icon;
        const isActive = currentStage === s.id;
        const isClickable = stageStates[s.id] !== undefined || s.id === currentStage;
        return (
          <button
            key={s.id}
            data-testid={`clean-step-${s.id}`}
            disabled={!isClickable}
            onClick={() => isClickable && onStageClick(s.id)}
            className={[
              'relative text-left pl-4 pr-2 py-3 rounded-lg transition-all',
              isActive ? 'bg-slate-900 text-white' : styles.bg + ' ' + styles.fg,
              isClickable ? 'cursor-pointer hover:ring-2 ' + styles.ring : 'opacity-50 cursor-not-allowed',
            ].join(' ')}
          >
            <div className="flex items-center gap-3">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono ${isActive ? 'bg-white/20 text-white' : styles.dot + ' text-white'}`}>
                {s.id}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm flex items-center gap-1.5">
                  {s.name}
                  {!isActive && st !== 'pending' && !isStale && (
                    <Ico className={`w-3.5 h-3.5 ${styles.fg}`} />
                  )}
                  {isStale && (
                    <RotateCw className={`w-3.5 h-3.5 ${isActive ? 'text-amber-300' : 'text-amber-600'}`} />
                  )}
                </div>
                <div className={`text-xs ${isActive ? 'text-slate-300' : 'opacity-70'}`}>{s.description}</div>
                {isStale && (
                  <div
                    data-testid={`clean-step-${s.id}-stale-badge`}
                    className={[
                      'mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider',
                      isActive ? 'bg-amber-300/25 text-amber-200' : 'bg-amber-100 text-amber-800',
                    ].join(' ')}
                  >
                    Re-run required
                  </div>
                )}
              </div>
            </div>
            {idx < STAGES.length - 1 && (
              <div className="absolute left-7 top-12 w-px h-3 bg-slate-300" />
            )}
          </button>
        );
      })}
    </nav>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Upload dropzone (pre-job)
// ═══════════════════════════════════════════════════════════════════
function UploadZone({ onStart, uploading }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDrag(e.type === 'dragenter' || e.type === 'dragover');
  }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f && f.name.match(/\.(csv|tsv|txt|xlsx|xls)$/i)) setFile(f);
    else toast.error('Upload CSV, TSV, or Excel only');
  }, []);

  return (
    <div data-testid="clean-upload-zone" className="max-w-2xl mx-auto mt-20">
      <Card className="border-2 border-dashed">
        <CardContent className="p-10"
          onDragEnter={handleDrag} onDragOver={handleDrag}
          onDragLeave={handleDrag} onDrop={handleDrop}
        >
          <div className={`text-center p-8 rounded-lg transition-colors ${drag ? 'bg-slate-100' : 'bg-slate-50'}`}>
            <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-lg font-medium text-slate-800 mb-1">Upload your dataset</p>
            <p className="text-sm text-slate-500 mb-4">CSV, TSV, or Excel · 30 MB max</p>
            <input id="clean-file-input" data-testid="clean-file-input" type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <label htmlFor="clean-file-input">
              <Button variant="outline" asChild data-testid="clean-choose-file-btn">
                <span className="cursor-pointer">{file ? file.name : 'Choose file'}</span>
              </Button>
            </label>
            <div className="mt-6">
              <Button data-testid="clean-start-btn" disabled={!file || uploading}
                onClick={() => file && onStart(file)}
                className="bg-slate-900 hover:bg-slate-700 text-white">
                {uploading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</>
                  : <>Start validation <ChevronRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-6">
            Your file will pass through a 6-stage validator: Structure → Missing → Duplicates → Types → Design → Outliers.
            Each stage surfaces problems honestly; you decide what to keep or drop.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Per-stage panels
// ═══════════════════════════════════════════════════════════════════
function StageHeader({ stage, result }) {
  const st = result?.status || 'pending';
  const styles = statusStyles[st];
  const Ico = styles.icon;
  return (
    <div data-testid={`clean-stage-${stage.id}-header`} className={`rounded-lg px-5 py-4 mb-5 ring-1 ${styles.bg} ${styles.ring}`}>
      <div className="flex items-start gap-3">
        <Ico className={`w-6 h-6 shrink-0 ${styles.fg}`} />
        <div className="flex-1">
          <div className={`text-xs uppercase tracking-wider font-semibold ${styles.fg}`}>Stage {stage.id} · {stage.name}</div>
          <div className="text-base font-medium text-slate-900 mt-0.5" data-testid={`clean-stage-${stage.id}-summary`}>{result?.summary || stage.description}</div>
        </div>
      </div>
    </div>
  );
}

function FindingsList({ findings }) {
  if (!findings?.length) return null;
  return (
    <div className="space-y-2" data-testid="clean-findings-list">
      {findings.map((f, i) => {
        const sev = f.severity || f.band || 'warn';
        const color = sev === 'block' || sev === 'drop_recommended' ? 'rose' : sev === 'pass' ? 'emerald' : 'amber';
        return (
          <div key={i} className={`flex items-start gap-3 rounded-md border border-${color}-200 bg-${color}-50 px-4 py-3 text-sm`}>
            <span className={`w-1.5 h-1.5 rounded-full bg-${color}-500 mt-2 shrink-0`} />
            <div className="flex-1 text-slate-800">
              {f.message || JSON.stringify(f)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DecisionPrompts({ prompts, decisions, onDecide }) {
  if (!prompts?.length) return null;
  return (
    <div className="mt-5 space-y-3" data-testid="clean-decision-prompts">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Decisions required</div>
      {prompts.map((p) => (
        <div key={p.key} className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-sm text-slate-800 mb-2">{p.prompt}</div>
          {p.blocked_reason && (
            <div
              className="mb-2 text-xs rounded bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1"
              data-testid={`clean-decision-${p.key}-blocked`}
            >
              ⓘ {p.blocked_reason}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {p.options?.map((opt) => {
              const selected = decisions[p.key] === opt;
              return (
                <button key={opt}
                  data-testid={`clean-decision-${p.key}-${opt}`}
                  onClick={() => onDecide(p.key, opt)}
                  className={[
                    'px-3 py-1 rounded text-xs font-medium transition-colors',
                    selected ? 'bg-slate-900 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700',
                  ].join(' ')}>
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function SentinelPrompts({ prompts, decisions, onSentinel }) {
  if (!prompts?.length) return null;
  return (
    <div className="mt-5 space-y-3" data-testid="clean-sentinel-prompts">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sentinel value review</div>
      {prompts.map((p) => {
        const curr = decisions[p.decision_key];
        return (
          <div key={p.decision_key} className="rounded-md border border-slate-200 bg-white px-4 py-3">
            <div className="text-sm text-slate-800 mb-2">
              Column <span className="font-mono text-slate-900">&apos;{p.column}&apos;</span> has <b>{p.count}</b> cell(s) with value <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{p.sentinel}</span>. Treat as missing?
            </div>
            <div className="flex gap-2">
              {[['Yes', true], ['No', false], ['Review', 'review']].map(([lbl, v]) => (
                <button key={lbl}
                  data-testid={`clean-sentinel-${p.decision_key}-${lbl}`}
                  onClick={() => onSentinel(p.decision_key, v)}
                  className={[
                    'px-3 py-1 rounded text-xs font-medium',
                    curr === v ? 'bg-slate-900 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700',
                  ].join(' ')}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ColumnRatesTable({ rates }) {
  if (!rates?.length) return null;
  return (
    <div className="mt-5 rounded-md border border-slate-200 overflow-hidden">
      <table data-testid="clean-column-rates" className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Column</th>
            <th className="text-right px-4 py-2 font-medium">Missing</th>
            <th className="text-right px-4 py-2 font-medium">Rate</th>
            <th className="text-left px-4 py-2 font-medium">Band</th>
          </tr>
        </thead>
        <tbody>
          {rates.map((r) => (
            <tr key={r.column} className="border-t border-slate-100">
              <td className="px-4 py-2 font-mono text-slate-800">{r.column}</td>
              <td className="px-4 py-2 text-right text-slate-700">{r.n_missing}</td>
              <td className="px-4 py-2 text-right text-slate-700">{(r.rate * 100).toFixed(1)}%</td>
              <td className="px-4 py-2">
                <span className={[
                  'px-2 py-0.5 rounded text-xs font-medium',
                  r.band === 'pass' ? 'bg-emerald-100 text-emerald-800' :
                  r.band === 'warn' ? 'bg-amber-100 text-amber-800' :
                  'bg-rose-100 text-rose-800',
                ].join(' ')}>{r.band}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StudyTypeSelector({ value, onChange }) {
  const options = [
    {
      key: 'Diversity / germplasm characterization',
      label: 'Diversity / germplasm characterization',
      hint: 'Recommended: Flag only — preserves all values; genetic spread is the signal you\'re studying',
    },
    {
      key: 'Varietal trial / yield evaluation with checks',
      label: 'Varietal trial / yield evaluation with checks',
      hint: 'Recommended: Per-trait NaN replacement — removes likely entry errors while preserving rows',
    },
    {
      key: 'Quality control / uniformity trial',
      label: 'Quality control / uniformity trial',
      hint: 'Recommended: Listwise — flags any plot with any abnormal trait',
    },
    {
      key: 'Custom — let me choose mode manually',
      label: 'Custom — let me choose mode manually',
      hint: null,
    },
  ];
  return (
    <div className="mt-5" data-testid="clean-study-type">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Study type
      </div>
      <div className="text-xs text-slate-500 mb-3">
        Outlier handling depends on whether genetic spread is the signal
        you&apos;re studying or noise to remove. Pick one:
      </div>
      <div className="space-y-2">
        {options.map((o) => (
          <label
            key={o.key}
            className="flex items-start gap-3 rounded-md border border-slate-200 bg-white px-4 py-2 cursor-pointer hover:bg-slate-50"
            data-testid={`clean-study-type-option-${o.key.split(' ')[0].toLowerCase()}`}
          >
            <input
              type="radio"
              name="study_type"
              value={o.key}
              checked={value === o.key}
              onChange={() => onChange(o.key)}
              className="accent-slate-900 mt-0.5"
            />
            <div className="flex-1">
              <div className="text-sm text-slate-800 font-medium">{o.label}</div>
              {o.hint && (
                <div className="text-xs text-slate-500 mt-0.5">{o.hint}</div>
              )}
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function DeletionModeSelector({ selected, onChange, recommendedMode, recommendedReason, nRows }) {
  const modes = [
    ['listwise', 'Listwise — remove row if any trait flags',
      'Loses ~N rows; use only with very large datasets'],
    ['per_trait', 'Per-trait — replace outlier cells with NaN only',
      'Preserves rows; marks suspicious cells as missing for that trait only'],
    ['winsorize', 'Winsorize — cap at IQR fence',
      'Caps outliers at the IQR boundary; keeps the row but modifies the value'],
    ['flag_only', 'Flag only — keep all values unchanged',
      'Marks outliers in the report only; data passed to analysis unchanged'],
  ];
  // Default tooltip copy when the parent doesn't pass an explicit
  // reason. Keeps the rationale honest about what triggered the
  // green pill (study-type rule). (Niranjan, Feb 2026)
  const defaultReason = (mode) => {
    switch (mode) {
      case 'flag_only':
        return "Recommended for diversity / germplasm studies — real biological extremes are the signal you're measuring; removing them destroys it.";
      case 'per_trait':
        return 'Recommended for varietal / yield trials with checks — drops likely entry errors per-trait while preserving rows for ANOVA balance.';
      case 'listwise':
        return 'Recommended for quality-control / uniformity trials — any plot with any abnormal trait is dropped to keep the trial uniform.';
      case 'winsorize':
        return 'Recommended when you must keep N constant for ANOVA but want to dampen extreme tails (caps to IQR fence).';
      default:
        return null;
    }
  };
  return (
    <div className="mt-5" data-testid="clean-deletion-mode">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
        Deletion mode
      </div>
      <div
        className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-3"
        data-testid="clean-deletion-mode-caption"
      >
        For diversity studies, real biological extremes are common and removing
        them destroys the signal you&apos;re measuring. Pick &quot;Flag only&quot;
        to keep all values and just mark suspicious cells for review.
      </div>
      <div className="space-y-2">
        {modes.map(([val, lbl, hint]) => {
          const isRecommended = recommendedMode === val;
          const tooltipText = isRecommended
            ? (recommendedReason || defaultReason(val) || 'Recommended for your study type.')
            : null;
          return (
            <label
              key={val}
              className={[
                'flex items-start gap-3 rounded-md border px-4 py-2 cursor-pointer transition-colors',
                isRecommended
                  ? 'border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50'
                  : 'border-slate-200 bg-white hover:bg-slate-50',
              ].join(' ')}
              data-testid={`clean-mode-row-${val}`}
            >
              <input
                type="radio"
                name="deletion_mode"
                value={val}
                data-testid={`clean-mode-${val}`}
                checked={selected === val}
                onChange={() => onChange(val)}
                className="accent-slate-900 mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm text-slate-800 font-medium flex items-center gap-2">
                  {lbl}
                  {isRecommended && (
                    <span
                      className="relative inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white cursor-help"
                      title={tooltipText}
                      data-testid={`clean-mode-${val}-recommended`}
                      aria-describedby={`clean-mode-${val}-recommended-tip`}
                    >
                      Recommended for your study
                      <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M8 1.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13Zm0 1.5a5 5 0 1 0 0 10A5 5 0 0 0 8 3Zm0 8a.75.75 0 1 1 0 1.5.75.75 0 0 1 0-1.5Zm0-6.75c1.105 0 2 .735 2 1.875 0 .708-.342 1.118-.928 1.583l-.21.16c-.27.205-.362.293-.362.532V8a.5.5 0 0 1-1 0v-.1c0-.526.245-.86.654-1.18l.222-.17c.42-.317.624-.503.624-.85 0-.42-.345-.875-1-.875s-1 .455-1 .875a.5.5 0 0 1-1 0c0-1.14.895-1.875 2-1.875Z"/>
                      </svg>
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
                {isRecommended && tooltipText && (
                  <div
                    id={`clean-mode-${val}-recommended-tip`}
                    className="mt-1 text-[11px] text-emerald-800 bg-emerald-50/60 border-l-2 border-emerald-500 pl-2 py-1"
                    data-testid={`clean-mode-${val}-recommended-reason`}
                  >
                    {tooltipText}
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>
      {nRows ? (
        <div className="text-[11px] text-slate-500 mt-2">
          Dataset size: {nRows} row(s).
          {nRows < 200
            ? ' Per-trait is generally preferred — small datasets can\'t afford row loss.'
            : nRows > 1000
              ? ' Listwise is also acceptable on datasets this large.'
              : ' Per-trait is generally preferred at this size.'}
        </div>
      ) : null}
    </div>
  );
}

function DisagreementBanners({ banners }) {
  if (!banners?.length) return null;
  return (
    <div className="mt-5 space-y-3" data-testid="clean-disagreement-banners">
      {banners.map((b, i) => (
        <div key={i} className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <b>IQR vs Z-score disagreement:</b> {b.message}
        </div>
      ))}
    </div>
  );
}

function FeedForwardBanners({ items, onRecompute }) {
  if (!items?.length) return null;
  return (
    <div className="mt-5 space-y-3" data-testid="clean-feedforward-banners">
      {items.map((f, i) => (
        <div key={i} className="rounded-md border border-slate-300 bg-slate-50 px-4 py-3 text-sm">
          <div className="text-slate-800">{f.message}</div>
          {f.action_available === 'recompute_without_flagged' && (
            <Button variant="outline" size="sm" className="mt-3"
              data-testid="clean-recompute-btn"
              onClick={onRecompute}>
              Recompute without flagged values
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}


function BiometricalSummary({ rows }) {
  if (!rows?.length) return null;
  const fmt = (v, digits = 2) => {
    if (v === null || v === undefined || Number.isNaN(v)) return '—';
    return Number(v).toFixed(digits);
  };
  const statusStyle = {
    ok:        { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'ok' },
    low:       { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'low' },
    high:      { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'high' },
    very_high: { bg: 'bg-rose-50',    text: 'text-rose-700',    label: 'very high' },
    unknown:   { bg: 'bg-slate-50',   text: 'text-slate-500',   label: 'no reference' },
  };
  const cited = new Set();
  rows.forEach((r) => {
    const src = r.assessment?.citation;
    if (src) cited.add(src);
  });
  return (
    <div className="mt-6" data-testid="clean-biometrical-summary">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm font-semibold text-slate-800">
          Biometrical summary
        </div>
        <div className="text-xs text-slate-500">
          CV% · SEm · CD₀.₀₅ per trait
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              {['Trait', 'n', 'Mean', 'SD', 'CV%', 'SEm', 'CD₀.₀₅', 'Assessment'].map((h) => (
                <th key={h} className="text-left px-3 py-2 font-medium text-slate-600">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const s = r.assessment || {};
              const st = statusStyle[s.status || 'unknown'];
              const ref = s.reference_range;
              return (
                <tr key={r.trait} className="border-t border-slate-100"
                    data-testid={`clean-bio-row-${r.trait}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{r.trait}</td>
                  <td className="px-3 py-2 text-slate-600">{r.n}</td>
                  <td className="px-3 py-2 text-slate-600 font-mono">{fmt(r.mean)}</td>
                  <td className="px-3 py-2 text-slate-600 font-mono">{fmt(r.sd)}</td>
                  <td className="px-3 py-2 text-slate-700 font-mono">{fmt(r.cv_pct, 1)}</td>
                  <td className="px-3 py-2 text-slate-600 font-mono">{fmt(r.sem)}</td>
                  <td className="px-3 py-2 text-slate-600 font-mono">{fmt(r.cd_p05)}</td>
                  <td className={`px-3 py-2 ${st.bg}`}>
                    <span className={`font-semibold ${st.text}`}>{st.label}</span>
                    {ref && (
                      <span className="ml-1 text-slate-500">
                        (ref {ref[0].toFixed(0)}–{ref[1].toFixed(0)}%)
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {cited.size > 0 && (
        <div className="mt-2 text-[11px] text-slate-500 space-y-0.5">
          {[...cited].map((src) => (
            <div key={src}>Source: <i>{src}</i></div>
          ))}
        </div>
      )}
      {rows.some((r) => (r.assessment?.status || 'unknown') === 'unknown') && (
        <div className="mt-2 text-[11px] text-slate-500">
          <i>No crop-specific reference ranges in the current
            database — "no reference" rows carry trial-error numbers
            only; judge against your trial's historical norms.</i>
        </div>
      )}
      {rows.some((r) => ['high', 'very_high'].includes(r.assessment?.status)) && (
        <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
          One or more traits carry a CV above the published range.
          High CV means elevated trial error — heritability estimates
          for those traits will be reduced, and plot-level issues
          (damage, heterogeneity) are worth investigating before
          interpretation.
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════

function CropSelector({ crop, onChange, onRecompute, running }) {
  const [local, setLocal] = useState(crop || '');
  useEffect(() => { setLocal(crop || ''); }, [crop]);
  return (
    <div
      className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3"
      data-testid="clean-crop-selector"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
            Crop (for CV reference lookup)
          </div>
          <div className="text-xs text-slate-600">
            Session 3a: scaffold only. The crop name is recorded in the
            cleaning report; reference ranges for sesame and other
            crops land in Session 3b/c as real users bring them.
          </div>
        </div>
        <input
          type="text"
          placeholder="e.g. sesame, rice, wheat"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={() => { if (local !== crop) onChange(local); }}
          className="px-3 py-1.5 text-sm rounded border border-slate-300 focus:border-slate-500 focus:outline-none bg-white"
          data-testid="clean-crop-input"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => { onChange(local); onRecompute(); }}
          disabled={running || local === crop}
          data-testid="clean-crop-apply"
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Recompute'}
        </Button>
      </div>
    </div>
  );
}


// Main page
// ═══════════════════════════════════════════════════════════════════
export default function DataCleaning() {
  const navigate = useNavigate();
  const [jobId, setJobId] = useState(null);
  const [filename, setFilename] = useState('');
  const [currentStage, setCurrentStage] = useState(1);
  const [stageResults, setStageResults] = useState({});    // {1: result, 2: result, …}
  const [decisionsByStage, setDecisionsByStage] = useState({}); // {2: {sentinel_treat_as_missing: {...}}, …}
  const [jobDecisions, setJobDecisions] = useState({});    // server-side decisions (incl. accept flags)
  const [revisionLog, setRevisionLog] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [forwarding, setForwarding] = useState(false);

  // ─── API helpers ─────────────────────────────────────────────
  const refreshJob = useCallback(async (jid) => {
    if (!jid) return;
    try {
      const { data } = await axios.get(`${API}/clean/validate/${jid}`, {
        withCredentials: true,
      });
      setStageResults(data.stages || {});
      setJobDecisions(data.decisions || {});
      setRevisionLog(data.revision_log || []);
    } catch (e) {
      // silent — next render will surface via toast on action
    }
  }, []);

  // Deep-link support: ?job_id=xxx re-enters a prior cleaning session
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const jid = qs.get('job_id');
    if (jid && !jobId) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/clean/validate/${jid}`, {
            withCredentials: true,
          });
          setJobId(jid);
          setFilename(data.filename || '');
          setStageResults(data.stages || {});
          setJobDecisions(data.decisions || {});
          setRevisionLog(data.revision_log || []);
          setCurrentStage(data.current_stage || 1);
        } catch {
          toast.error('Could not reopen cleaning session — job not found.');
        }
      })();
    }
  }, [jobId]);

  // ─── API helpers ─────────────────────────────────────────────
  const handleStart = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/clean/validate/start`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJobId(data.job_id);
      setFilename(data.filename);
      setStageResults({ 1: data.stage_result });
      setCurrentStage(1);
      setDecisionsByStage({});
      toast.success('File parsed — Stage 1 complete');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const runStage = async (stageNum, accept = false, decisionsOverride = null) => {
    if (!jobId) return;
    setRunning(true);
    const decisions = decisionsOverride !== null
      ? decisionsOverride
      : (decisionsByStage[stageNum] || {});
    try {
      const { data } = await axios.post(
        `${API}/clean/validate/stage/${stageNum}`,
        { job_id: jobId, stage: stageNum, decisions, accept_and_continue: accept },
        { withCredentials: true },
      );
      // Always refetch full job so `stale` flags on downstream stages
      // (set server-side when decisions are revised) land in state.
      await refreshJob(jobId);
      if (data.invalidated_downstream?.length) {
        toast.warning(
          `Stage ${stageNum} was revised — stages ${data.invalidated_downstream.join(', ')} are now stale and must be re-run.`,
        );
      }
      return data.stage_result;
    } catch (e) {
      toast.error(e.response?.data?.detail || `Stage ${stageNum} failed`);
    } finally {
      setRunning(false);
    }
  };

  const handleContinue = async () => {
    const curr = stageResults[currentStage];
    if (!curr) return;
    // If warn, accept-and-continue. If pass, just advance.
    if (curr.status === 'warn') {
      await runStage(currentStage, true);
    }
    const next = currentStage + 1;
    if (next > 6) {
      toast.success('All 6 stages complete — validation finished.');
      return;
    }
    const r = await runStage(next);
    if (r) setCurrentStage(next);
  };

  const handleRerun = async () => {
    await runStage(currentStage);
  };

  const handleStageClick = (n) => {
    if (stageResults[n] !== undefined) setCurrentStage(n);
  };

  const handleDecision = (key, val) => {
    setDecisionsByStage((d) => ({
      ...d,
      [currentStage]: { ...(d[currentStage] || {}), [key]: val },
    }));
  };

  const handleSentinel = (key, val) => {
    setDecisionsByStage((d) => ({
      ...d,
      [currentStage]: {
        ...(d[currentStage] || {}),
        sentinel_treat_as_missing: {
          ...(d[currentStage]?.sentinel_treat_as_missing || {}),
          [key]: val,
        },
      },
    }));
  };

  const handleDeletionMode = (mode) => {
    // P1 (Niranjan): when user picks "Diversity / germplasm
    // characterization" study_type AND then explicitly switches the
    // deletion mode to anything other than `flag_only`, ask for
    // confirmation. Removing outliers from a diversity panel can
    // discard real genotypic variation. Backend honours the override
    // via `deletion_mode_override=true` and surfaces a permanent
    // diversity_warning finding so the choice is documented.
    const cur = decisionsByStage[currentStage] || {};
    const isDiversity =
      cur.study_type === 'Diversity / germplasm characterization';
    if (isDiversity && mode !== 'flag_only') {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Removing outliers from a diversity panel may discard real '
        + 'genotypic variation. Are you sure you want to use the '
        + `'${mode}' mode instead of 'flag_only'?`,
      );
      if (!ok) return;
    }
    setDecisionsByStage((d) => ({
      ...d,
      [currentStage]: {
        ...(d[currentStage] || {}),
        deletion_mode: mode,
        // Mark as a manual override so the backend doesn't override
        // it back to the study-type-recommended mode on the next run.
        deletion_mode_override: true,
      },
    }));
  };

  const handleStudyType = (studyType) => {
    // Picking a study_type clears any prior manual deletion-mode
    // override — the new study type's recommended mode will take
    // effect on the next stage run.
    const newDecisions = {
      ...(decisionsByStage[6] || {}),
      study_type: studyType,
      deletion_mode_override: false,
    };
    setDecisionsByStage((d) => ({ ...d, 6: newDecisions }));
    // Re-run Stage 6 with the new decisions explicitly — React state
    // updates are async so passing the closure-captured value would
    // race the setState call.
    runStage(6, false, newDecisions);
  };

  const handleCrop = (crop) => {
    setDecisionsByStage((d) => ({
      ...d,
      6: { ...(d[6] || {}), crop },
    }));
  };

  const handleRecompute = async () => {
    setDecisionsByStage((d) => ({
      ...d,
      6: { ...(d[6] || {}), exclude_stage4_flagged: true },
    }));
    // Run with the new decision
    setRunning(true);
    try {
      await axios.post(
        `${API}/clean/validate/stage/6`,
        { job_id: jobId, stage: 6,
          decisions: { ...(decisionsByStage[6] || {}), exclude_stage4_flagged: true },
          accept_and_continue: false },
        { withCredentials: true },
      );
      await refreshJob(jobId);
      toast.success('Outliers recomputed without Stage-4 flagged rows');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recompute failed');
    } finally {
      setRunning(false);
    }
  };

  // ─── Session-2: Export + Forward hand-off ────────────────────
  const handleExport = async (fmt) => {
    if (!jobId) return;
    setExporting(fmt);
    try {
      const { data, headers } = await axios.get(
        `${API}/clean/validate/${jobId}/export/${fmt}`,
        { withCredentials: true, responseType: 'blob' },
      );
      // Derive a filename from content-disposition if present
      const cd = headers['content-disposition'] || '';
      const m = /filename="([^"]+)"/.exec(cd);
      const base = filename.replace(/\.[^.]+$/, '') || 'data';
      const defaultName =
        fmt === 'pdf' ? `${base}_cleaning_report.pdf`
        : fmt === 'xlsx' ? `${base}_cleaned.xlsx`
        : `${base}_cleaned.csv`;
      const dlName = m ? m[1] : defaultName;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', dlName);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} download started.`);
    } catch (e) {
      // Blob error responses need special parsing
      let detail = 'Export failed';
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          detail = j.detail || text;
        } catch { /* ignore */ }
      } else {
        detail = e.response?.data?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setExporting(null);
    }
  };

  const handleForward = async () => {
    if (!jobId) return;
    setForwarding(true);
    try {
      const { data } = await axios.post(
        `${API}/clean/validate/${jobId}/forward`,
        {},
        { withCredentials: true },
      );
      // Stash the cleaned CSV in sessionStorage so the Biometrical
      // Analysis page can auto-ingest + show the audit banner.
      const handoff = {
        job_id: data.job_id,
        filename: data.filename,
        csv_b64: data.csv_b64,
        timestamp: new Date().toISOString(),
        cleaning_log_summary: data.cleaning_log_summary,
        n_rows: data.n_rows,
        n_cols: data.n_cols,
      };
      try {
        sessionStorage.setItem('bk_clean_handoff', JSON.stringify(handoff));
      } catch (e) {
        toast.error('Browser storage full — cannot carry cleaned data across tabs.');
        return;
      }
      toast.success(`Cleaned ${data.n_rows} × ${data.n_cols} carried to Analysis.`);
      navigate('/biometrical-analysis');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forward hand-off failed');
    } finally {
      setForwarding(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────
  const bg = 'min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100';

  if (!jobId) {
    return (
      <div className={bg}>
        <SharedNav />
        <div className="max-w-6xl mx-auto px-6 py-10">
          <header className="mb-8">
            <h1 className="text-3xl font-semibold text-slate-900" data-testid="clean-page-title">Clean — 6-stage validator</h1>
            <p className="text-slate-600 mt-2 max-w-2xl">Run your raw dataset through structure, missing-data, duplicate, type-range, design, and outlier checks. No silent mutations; every decision is yours.</p>
          </header>
          <UploadZone onStart={handleStart} uploading={uploading} />
        </div>
      </div>
    );
  }

  const currentResult = stageResults[currentStage];
  const currentStageMeta = STAGES.find((s) => s.id === currentStage);
  const currentDecisions = decisionsByStage[currentStage] || {};
  const currentIsStale = !!currentResult?.stale;
  // Decision-pending guard. The server returns `decision_pending: true`
  // when a required prompt hasn't been answered. When the user clicks
  // an option locally, we don't immediately re-run the stage — we
  // just store the pick in `decisionsByStage`. So we must clear the
  // pending flag locally too once the user has selected a resolving
  // option for any of the open prompts.
  const _localResolvesPending = (currentResult?.decisions_required || [])
    .every((p) => {
      const localPick = currentDecisions[p.key];
      // 'Show me what was detected' is a preview, not a real choice
      if (p.key === 'two_row_header_action') {
        return localPick === 'Yes, convert to long format'
            || localPick === 'No, keep as-is';
      }
      return localPick != null && localPick !== '';
    });
  const currentDecisionPending = !!currentResult?.decision_pending
    && !_localResolvesPending;
  const canContinue = currentResult
    && currentResult.status !== 'block'
    && !currentIsStale
    && !currentDecisionPending;
  // All six stages must exist, be non-stale, non-block, and any warn
  // status must have been accepted server-side.
  const allSixReady = [1, 2, 3, 4, 5, 6].every((n) => {
    const r = stageResults[n];
    if (!r) return false;
    if (r.stale) return false;
    if (r.status === 'block') return false;
    if (r.status === 'warn' && !jobDecisions[`stage_${n}_accepted`]) return false;
    return true;
  });
  const anyStale = [1, 2, 3, 4, 5, 6].some((n) => stageResults[n]?.stale);

  // P0 fix: when the user explicitly chose "No, keep as-is" at
  // Stage 1, downstream stages must surface a permanent warning so
  // the high-missing-rate / Unnamed-N artefacts don't look like
  // a platform bug.
  const stage1Decision =
    (jobDecisions['1'] && jobDecisions['1'].two_row_header_action) ||
    null;
  const keptAsIs = stage1Decision === 'No, keep as-is';

  return (
    <div className={bg}>
      <SharedNav />
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="clean-page-title">Clean — validating <span className="font-mono text-slate-600">{filename}</span></h1>
          <p className="text-slate-500 text-sm mt-1">Job {jobId.slice(0, 8)}</p>
        </header>

        <div className="flex items-start gap-6">
          <Stepper
            currentStage={currentStage}
            stageStates={stageResults}
            onStageClick={handleStageClick}
          />

          <main className="flex-1 min-w-0">
            {!currentResult ? (
              <div data-testid="clean-stage-loading" className="flex items-center justify-center py-24 text-slate-500">
                <Loader2 className="w-5 h-5 mr-3 animate-spin" /> Running Stage {currentStage}…
              </div>
            ) : (
              <>
                <StageHeader stage={currentStageMeta} result={currentResult} />

                {currentDecisionPending && (
                  <div
                    data-testid="clean-decision-pending-banner"
                    className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
                  >
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-700" />
                    <div className="flex-1">
                      <div className="font-semibold">Decision required</div>
                      <div className="text-amber-800 mt-0.5">
                        {currentResult.pending_decision_message ||
                          'Pick one of the options below before continuing.'}
                      </div>
                    </div>
                  </div>
                )}

                {keptAsIs && currentStage > 1 && (
                  <div
                    data-testid="clean-kept-as-is-banner"
                    className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
                  >
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-700" />
                    <div className="flex-1">
                      <div className="font-semibold">
                        Original wide-format layout retained
                      </div>
                      <div className="text-amber-800 mt-0.5">
                        You chose to keep the original wide-format layout at
                        Stage 1. Stage 1 detected a two-row header which was
                        NOT converted. The "Unnamed:" columns and high
                        missing-data rates below are expected consequences
                        of this choice and do not reflect platform errors.
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 border-amber-400 text-amber-900 hover:bg-amber-100"
                        data-testid="clean-kept-as-is-go-back"
                        onClick={() => setCurrentStage(1)}
                      >
                        Re-run Stage 1 to convert
                      </Button>
                    </div>
                  </div>
                )}

                {currentIsStale && (
                  <div
                    data-testid="clean-stale-banner"
                    className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
                  >
                    <RotateCw className="w-5 h-5 shrink-0 mt-0.5 text-amber-700" />
                    <div className="flex-1">
                      <div className="font-semibold">Re-run required</div>
                      <div className="text-amber-800 mt-0.5">
                        {currentResult.stale_reason ||
                          'Upstream decisions were revised after this stage last ran. Re-run this stage against the current upstream state before continuing.'}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3 border-amber-400 text-amber-900 hover:bg-amber-100"
                        data-testid="clean-stale-rerun-btn"
                        disabled={running}
                        onClick={handleRerun}
                      >
                        <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Re-run this stage now
                      </Button>
                    </div>
                  </div>
                )}

                {/* Stage 1 extras */}
                {currentStage === 1 && currentResult.meta && (
                  <div data-testid="clean-stage-1-meta" className="rounded-md border border-slate-200 bg-white px-4 py-3 mb-4 text-sm">
                    <div className="grid grid-cols-3 gap-3">
                      <div><span className="text-slate-500">Encoding:</span> <span className="font-mono">{currentResult.meta.encoding}</span></div>
                      <div><span className="text-slate-500">Delimiter:</span> <span className="font-mono">{currentResult.meta.delimiter}</span></div>
                      <div><span className="text-slate-500">Trimmed blanks:</span> <span className="font-mono">{currentResult.meta.trimmed_blank_rows} rows · {currentResult.meta.trimmed_blank_cols} cols</span></div>
                    </div>
                  </div>
                )}

                {/* Stage 2: sentinel prompts + col rates */}
                {currentStage === 2 && (
                  <>
                    <SentinelPrompts
                      prompts={currentResult.sentinel_prompts}
                      decisions={currentDecisions.sentinel_treat_as_missing || {}}
                      onSentinel={handleSentinel}
                    />
                    <ColumnRatesTable rates={currentResult.column_rates} />
                  </>
                )}

                {/* Stage 6: mode, disagreement, feed-forward, biometrical */}
                {currentStage === 6 && (
                  <>
                    <StudyTypeSelector
                      value={currentDecisions.study_type || ''}
                      onChange={handleStudyType}
                    />
                    {currentResult.diversity_warning && (
                      <div
                        data-testid="clean-diversity-warning"
                        className="mt-4 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900 flex items-start gap-3"
                      >
                        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-rose-700" />
                        <div className="flex-1">
                          <div className="font-semibold">
                            Diversity panel + aggressive outlier mode
                          </div>
                          <div className="text-rose-800 mt-0.5">
                            {currentResult.diversity_warning.message}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3 border-rose-400 text-rose-900 hover:bg-rose-100"
                            data-testid="clean-diversity-revert-flag-only"
                            onClick={() => handleDeletionMode('flag_only')}
                          >
                            Switch to Flag only
                          </Button>
                        </div>
                      </div>
                    )}
                    <DeletionModeSelector
                      selected={currentResult.deletion_mode || 'per_trait'}
                      onChange={handleDeletionMode}
                      recommendedMode={currentResult.recommended_mode_from_study_type}
                      recommendedReason={
                        currentResult.recommended_mode_reason
                        || currentResult.study_type_recommendation_reason
                        || null
                      }
                      nRows={
                        currentResult?.outlier_results?.n_rows
                        || currentResult?.summary?.n_rows
                        || null
                      }
                    />
                    <DisagreementBanners banners={currentResult.disagreement_banners} />
                    <FeedForwardBanners
                      items={currentResult.feed_forward}
                      onRecompute={handleRecompute}
                    />
                    <BiometricalSummary
                      rows={currentResult.biometrical_summary}
                    />
                    <CropSelector
                      crop={currentDecisions.crop || ''}
                      onChange={handleCrop}
                      onRecompute={() => runStage(6)}
                      running={running}
                    />
                  </>
                )}

                {/* Generic findings + decisions */}
                <div className="mt-5">
                  <FindingsList findings={currentResult.findings?.filter((f) => !['iqr_vs_z_disagreement', 'stage4_feed_forward'].includes(f.type))} />
                </div>
                <DecisionPrompts
                  prompts={(currentResult.decisions_required || []).filter(
                    (p) => p.key !== 'study_type'
                  )}
                  decisions={currentDecisions}
                  onDecide={handleDecision}
                />

                {/* Action bar */}
                <div className="mt-6 flex items-center justify-between gap-3 pt-4 border-t border-slate-200" data-testid="clean-action-bar">
                  <Button variant="outline" disabled={currentStage === 1 || running}
                    data-testid="clean-back-btn"
                    onClick={() => setCurrentStage(currentStage - 1)}>
                    Go back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="ghost" disabled={running}
                      data-testid="clean-rerun-btn"
                      onClick={handleRerun}>
                      Re-run this stage
                    </Button>
                    <Button
                      data-testid="clean-continue-btn"
                      disabled={
                        !canContinue ||
                        running ||
                        (currentStage === 6 &&
                          (currentResult.status !== 'warn' ||
                           jobDecisions.stage_6_accepted))
                      }
                      title={
                        currentDecisionPending
                          ? (currentResult.pending_decision_message
                              || 'Please choose how to handle the multi-row header before continuing.')
                          : undefined
                      }
                      onClick={handleContinue}
                      className="bg-slate-900 hover:bg-slate-700 text-white">
                      {currentStage === 6
                        ? (currentResult.status === 'warn'
                            ? (jobDecisions.stage_6_accepted ? 'Accepted' : 'Accept stage 6')
                            : 'Stage 6 complete')
                        : (currentResult.status === 'warn' ? 'Accept and continue' : 'Continue')}
                      {currentStage < 6 && <ChevronRight className="w-4 h-4 ml-1" />}
                    </Button>
                  </div>
                </div>

                {currentStage === 6 && currentResult.status !== 'block' && (
                  <div
                    className="mt-8 rounded-lg border border-slate-200 bg-white p-5"
                    data-testid="clean-export-panel"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                      <div>
                        <div className="text-base font-semibold text-slate-900">
                          Validation pipeline complete
                        </div>
                        <div className="text-sm text-slate-600 mt-0.5">
                          {allSixReady
                            ? 'All six stages passed or were explicitly accepted. You can download the cleaned dataset and the methods-section cleaning report, or carry the cleaned data directly into Analysis.'
                            : anyStale
                              ? 'One or more stages are stale — re-run them before downloading. Exports will be blocked until every stage is current.'
                              : 'One or more stages still need review. Accept warnings or resolve blocks before exporting.'}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                      {[
                        { fmt: 'csv',  label: 'Cleaned CSV',   Icon: Download },
                        { fmt: 'xlsx', label: 'Cleaned XLSX',  Icon: Download },
                        { fmt: 'pdf',  label: 'Cleaning Report PDF', Icon: FileText },
                      ].map(({ fmt, label, Icon }) => (
                        <Button
                          key={fmt}
                          variant="outline"
                          disabled={!allSixReady || exporting === fmt}
                          onClick={() => handleExport(fmt)}
                          data-testid={`clean-export-${fmt}`}
                          className="justify-start"
                        >
                          {exporting === fmt
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Icon className="w-4 h-4 mr-2" />}
                          {label}
                        </Button>
                      ))}
                    </div>

                    <Button
                      disabled={!allSixReady || forwarding}
                      onClick={handleForward}
                      data-testid="clean-forward-btn"
                      className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                    >
                      {forwarding
                        ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing cleaned data…</>
                        : <>Continue to Analysis <ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>

                    {revisionLog.length > 0 && (
                      <div
                        className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3"
                        data-testid="clean-revision-log-summary"
                      >
                        This session includes {revisionLog.length} decision revision
                        {revisionLog.length === 1 ? '' : 's'} — the cleaning report
                        records every change for audit.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
